import React, { useEffect, useState } from 'react'
import { Button, Col, Form, Input, Modal, Row } from 'antd'
import { Editor } from 'react-draft-wysiwyg'
import { convertToRaw, EditorState, ContentState } from 'draft-js'
import draftToHtml from 'draftjs-to-html'
import htmlToDraft from 'html-to-draftjs'

import { sendEmail } from '../../../services/others/quotation-email-services'
import { getSelectOptions } from '../../../components/common/common-form/common-form-select'
import SweetAlertService from '../../../services/lib/utils/sweet-alert-service'
import orderSummaryPdfGenerate from '../../static/pdf/order-summary/order-summary-pdf-generate'
import type { ApprovalOrder } from './order-approved-page'

// 订单确认邮件，界面和流程参考 PO 页面用的 components/common/email-dialog：
// Sender / Receiver / Subject + 富文本正文，Order Summary PDF 作为附件，可以点链接查看。
// 只有点 Send 才调用发邮件接口

const tailLayout = {
  wrapperCol: { offset: 22 }
}

// 后端 Common/SendEmail 会用 SenderAddress 作为实际发件邮箱（Resend 只认已验证域名），
// 订单确认邮件固定从 order@jadcup.co.nz 发出，不允许修改
const senderAddress = 'order@jadcup.co.nz'

const defaultHtml = (order: ApprovalOrder) =>
  `<p>Dear ${order.customer.company || 'Customer'},</p>
  <p>Thank you for your order. Your order <b>${order.orderNo}</b>${order.custOrderNo ? ` (PO: ${order.custOrderNo})` : ''} has been confirmed.</p>
  <p>Please find the order summary attached. If any of the details are incorrect, please contact us as soon as possible.</p>
  <p></p>
  <p>Kind Regards,</p>
  <p></p>
  <p>Jadcup</p>
  <p>Email: order@jadcup.co.nz | Phone: 09 282 3988</p>
  <p>Jadcup, 17b Landing Drive, Māngere, Auckland 2022, New Zealand</p>`

// Receiver 默认值：订单配送地址的联系人邮箱，没有再用客户资料里的 email。
// 订单里存的是地址文本，和抽屉一样按 address 文本在客户的配送地址里匹配
const getReceiverEmail = async (raw: any): Promise<string> => {
  const customerEmail = raw.customer?.email || ''
  try {
    const addresses = await getSelectOptions('', 'ExtraAddress/GetAllExtraAddress?customerId=' + raw.customerId)
    const address = (addresses || []).find((row: any) => row.address === raw.deliveryAddress)
    return address?.email?.trim() || customerEmail
  } catch (e) {
    return customerEmail
  }
}

// progress：批量审批后逐封弹出时显示第几封，如 "2/5"
const OrderApprovedEmailModal = (props: { order: ApprovalOrder | null; visible: boolean; progress?: string; onCancel: () => void }) => {
  const { order, visible, progress, onCancel } = props
  const [form] = Form.useForm()
  const [editorState, setEditorState] = useState(EditorState.createEmpty())
  const [receiverLoading, setReceiverLoading] = useState(false)
  const [blob, setBlob] = useState<Blob | null>(null)
  const [sending, setSending] = useState(false)

  // 每次打开都用当前订单数据重新生成 PDF 和默认正文
  useEffect(() => {
    if (!visible || !order) return
    const contentBlock = htmlToDraft(defaultHtml(order))
    if (contentBlock) {
      setEditorState(EditorState.createWithContent(ContentState.createFromBlockArray(contentBlock.contentBlocks)))
    }
    setBlob(null)
    orderSummaryPdfGenerate(order.raw).then(setBlob)
    setReceiverLoading(true)
    getReceiverEmail(order.raw).then(email => {
      form.setFieldsValue({ TargetAddress: email })
      setReceiverLoading(false)
    })
  }, [visible, order])

  if (!order) return null

  const fileName = `${order.customer.customerCode || 'Order'}_${order.orderNo}.pdf`

  const pdfShow = () => {
    if (!blob) return
    window.open(URL.createObjectURL(blob), '_blank')
  }

  const formFinishHandler = (values: any) => {
    if (!blob) return
    const formData = new FormData()
    formData.append('File', blob, fileName)
    formData.append('SenderName', 'Jadcup')
    formData.append('SenderAddress', senderAddress)
    formData.append('TargetName', order.customer.company || '')
    formData.append('TargetAddress', values.TargetAddress)
    formData.append('Subject', values.Subject)
    formData.append('HtmlContent', draftToHtml(convertToRaw(editorState.getCurrentContent())))
    formData.append('FileName', fileName)

    setSending(true)
    sendEmail(formData).then((res: any) => {
      // 接口返回 bool，false 表示 Resend 发送失败
      if (res?.data === false) {
        SweetAlertService.errorMessage('Send email fail!')
        return
      }
      SweetAlertService.successMessage(`Email sent to ${values.TargetAddress}`)
      onCancel()
    }).finally(() => setSending(false))
  }

  return (
    <Modal title={`Email - ${order.orderNo}${progress ? ` (${progress})` : ''}`} centered width={1000} visible={visible} onCancel={onCancel}
           destroyOnClose={true} maskClosable={false} footer={null}>
      <Form form={form} onFinish={formFinishHandler}>
        <Row>
          <Col span={10}>
            <Form.Item label="Sender">
              <Input value={senderAddress} disabled />
            </Form.Item>
          </Col>
          <Col span={10} offset={2}>
            <Form.Item label="Receiver" name="TargetAddress"
                       rules={[{ required: true, message: 'Customer email is required' }]}>
              <Input placeholder={receiverLoading ? 'Loading…' : 'Receiver'} disabled={receiverLoading} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label="Subject" name="Subject" initialValue={`Order Confirmation ${order.orderNo}`}
                   rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Editor
          editorState={editorState}
          toolbar={{
            options: ['inline', 'blockType', 'fontSize', 'fontFamily', 'list', 'textAlign', 'colorPicker', 'link', 'emoji', 'history'],
            list: { inDropdown: true },
            textAlign: { inDropdown: true },
            link: { inDropdown: true },
            history: { inDropdown: true }
          }}
          toolbarClassName="toolbarClassName"
          wrapperClassName="demo-wrapper"
          editorClassName="demo-editor"
          editorStyle={{ height: 400, border: '1px solid #E0E0E0' }}
          onEditorStateChange={setEditorState}
        />
        {blob ? <a onClick={pdfShow}>{fileName}</a> : <span style={{ color: '#8c8c8c' }}>Generating PDF…</span>}
        <Form.Item {...tailLayout}>
          <Button type="primary" htmlType="submit" loading={sending} disabled={!blob || receiverLoading}>
            Send
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default OrderApprovedEmailModal
